import React, { useEffect, useState } from 'react';
import './styles.css';
import NavRail from './NavRail';
import { TweaksPanel, TweakSection, TweakRadio, useTweaks } from './TweaksPanel';
import { mergeDefaults } from './themePersist';
import {
  CLUSTERS, REQUIRED_SLOTS, OPTIONAL_SLOTS, MASTER, RATES,
  Cluster, Slot, Master, Rate,
} from './uploadData';

const TWEAK_DEFAULTS = {
  theme: 'paper' as 'paper' | 'light' | 'dark',
  density: 'regular' as 'compact' | 'regular' | 'comfy',
  variant: 'safe' as 'safe' | 'bold',
};

function TitleBar({ cluster }: { cluster: Cluster }) {
  return (
    <div className="titlebar">
      <span className="brand">XELENCE</span>
      <span className="sep">/</span>
      <span className="crumb">Upload</span>
      <span className="sep">/</span>
      <span className="crumb"><b>{cluster.company}</b> · {cluster.period}</span>
      <div className="right">
        <div className="stat"><span className="k">cluster</span><span className="v">{cluster.id}</span></div>
        <div className="stat"><span className="k">files</span><span className="v">{cluster.filled}/{cluster.total}</span></div>
        <div className="stat"><span className="k">last upload</span><span className="v">28 апр 11:04</span></div>
      </div>
    </div>
  );
}

function ClusterPicker({ clusters, active, setActive }: {
  clusters: Cluster[]; active: string; setActive: (id: string) => void;
}) {
  return (
    <div className="cluster-grid">
      {clusters.map((c) => {
        const complete = c.filled === c.total;
        const pct = (c.filled / c.total) * 100;
        return (
          <div
            key={c.id}
            className={`cluster-card ${active === c.id ? 'active' : ''} ${complete ? 'complete' : 'partial'}`}
            onClick={() => setActive(c.id)}
          >
            <div className="top">
              <span className="label">{c.label}</span>
              <span className="progress">{c.filled}/{c.total}</span>
            </div>
            <div className="company">{c.company}</div>
            <div className="period">{c.period}</div>
            <div className="bar"><div className="bar-fill" style={{ width: pct + '%' }} /></div>
          </div>
        );
      })}
      <div
        className="cluster-card"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', borderLeft: '1px dashed var(--rule)' }}
      >
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>+ новый кластер</span>
      </div>
    </div>
  );
}

function SlotRow({ slot, required }: { slot: Slot; required: boolean }) {
  const status =
    slot.planned ? 'planned' :
    !slot.file ? 'empty' :
    slot.valid === 'warn' ? 'warn' :
    slot.valid === false ? 'err' :
    slot.warn ? 'warn' :
    'ok';
  return (
    <div className={`slot-row s-${status}`}>
      <div className="c-dot"><span className="d" /></div>
      <div className="c-name">
        <span>{slot.name}</span>
        <span className={`req-tag ${required ? 'req' : ''}`}>{required ? 'обяз.' : 'опц.'}</span>
      </div>
      <div className="c-desc">
        {slot.desc}
        {slot.warn && <span className="warn-msg">· {slot.warn}</span>}
        {slot.validMsg && <span className="warn-msg">· {slot.validMsg}</span>}
      </div>
      <div className={`c-file ${!slot.file ? 'empty' : ''}`}>
        {slot.file || (slot.planned ? '— запланирован —' : 'не загружен')}
      </div>
      <div className="c-size">{slot.size || ''}</div>
      <div className="c-sheets">
        {slot.sheets ? `${slot.sheets} лист${slot.sheets === 1 ? '' : slot.sheets < 5 ? 'а' : 'ов'}` : ''}
      </div>
      <div className="c-mod">{slot.modified || ''}</div>
      <div className="c-actions">
        {slot.file ? (
          <>
            <button>👁 preview</button>
            <button className="primary">⇪ replace</button>
          </>
        ) : slot.planned ? (
          <button disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>скоро</button>
        ) : (
          <button className="primary">+ upload</button>
        )}
      </div>
    </div>
  );
}

function SlotTable({ slots, required }: { slots: Slot[]; required: boolean }) {
  return (
    <div className="slot-table">
      <div className="thead">
        <div></div>
        <div>Слот</div>
        <div>Описание</div>
        <div>Файл</div>
        <div className="n">Размер</div>
        <div>Листы</div>
        <div>Загружено</div>
        <div></div>
      </div>
      {slots.map((s) => <SlotRow key={s.id} slot={s} required={required} />)}
    </div>
  );
}

function MasterBlock({ master }: { master: Master }) {
  return (
    <div className="master-block">
      <div className="glyph">★</div>
      <div>
        <div className="name">
          {master.file}
          <span className="ver">{master.version}</span>
        </div>
        <div className="sub">{master.note}</div>
      </div>
      <div className="stats">
        <div><b>{master.size}</b> · {master.sheets} листов</div>
        <div>обновлён {master.modified} · {master.uploadedBy}</div>
      </div>
      <div className="acts">
        <button>👁 preview</button>
        <button className="primary">⇪ replace</button>
      </div>
    </div>
  );
}

function RatesPanel({ rates }: { rates: Rate[] }) {
  const HIST: Record<string, { q4: number; q3: number; q2: number; q1: number }> = {
    USD: { q4: 514.20, q3: 502.80, q2: 488.60, q1: 471.30 },
    EUR: { q4: 552.40, q3: 540.10, q2: 524.80, q1: 506.10 },
    RUB: { q4: 5.48,   q3: 5.32,   q2: 5.21,   q1: 4.94   },
    CNY: { q4: 71.20,  q3: 70.40,  q2: 68.80,  q1: 65.90  },
    TRY: { q4: 14.40,  q3: 13.80,  q2: 12.90,  q1: 11.70  },
  };
  return (
    <div className="rates-panel">
      <div className="rp-head">
        <div className="rp-h1">Справочник курсов · общий на период</div>
        <div className="rp-h2">Применяется ко всем кластерам и конфигам · Q1 2026</div>
        <div className="rp-actions">
          <button>↻ синхр. с НБ РК</button>
          <button>📜 история изменений</button>
          <button className="primary">⇪ загрузить вручную</button>
        </div>
      </div>
      <div className="rates-wide">
        <div className="rwh">
          <div></div>
          <div>Валюта</div>
          <div className="n">Покупка</div>
          <div className="n">Продажа</div>
          <div className="n">Q4 25</div>
          <div className="n">Q3 25</div>
          <div className="n">Q2 25</div>
          <div className="n">Q1 25</div>
          <div>Источник</div>
          <div>Статус</div>
        </div>
        {rates.map((r) => {
          const h = HIST[r.code] || ({} as { q1?: number });
          const yoy = h.q1 ? ((r.sell - h.q1) / h.q1) * 100 : 0;
          return (
            <div key={r.code} className={`rwr ${!r.frozen ? 'editable' : ''}`}>
              <div className="code">{r.code}</div>
              <div className="name">
                {r.name}
                <span className="yoy" style={{ color: yoy >= 0 ? 'var(--diff)' : 'var(--match)' }}>
                  · YoY {yoy >= 0 ? '+' : ''}{yoy.toFixed(1)}%
                </span>
              </div>
              <div className="v n">{r.buy.toFixed(2)}</div>
              <div className="v n curr">{r.sell.toFixed(2)}</div>
              <div className="v n hist">{h.q4 ? h.q4.toFixed(2) : '—'}</div>
              <div className="v n hist">{h.q3 ? h.q3.toFixed(2) : '—'}</div>
              <div className="v n hist">{h.q2 ? h.q2.toFixed(2) : '—'}</div>
              <div className="v n hist">{h.q1 ? h.q1.toFixed(2) : '—'}</div>
              <div className="src">{r.source}</div>
              <div className="lock">{r.frozen ? '🔒 фикс.' : '✎ ручной'}</div>
            </div>
          );
        })}
      </div>
      <div className="rp-foot">
        <span>применяется в кластерах:</span>
        <b>dd · sd · kl · ki · dk · cn</b>
        <span className="sep">·</span>
        <span>пересчёт автоматически после изменения курса</span>
        <span className="meta">обновлено 30 апр 14:22 · Г. Касенова</span>
      </div>
    </div>
  );
}

export default function Upload() {
  const [t, setTweak] = useTweaks(mergeDefaults(TWEAK_DEFAULTS));
  const [activeCluster, setActiveCluster] = useState('dd_1q26');
  const [dragging, setDragging] = useState(false);
  const cluster = CLUSTERS.find((c) => c.id === activeCluster) || CLUSTERS[0];

  useEffect(() => {
    let counter = 0;
    const onEnter = (e: DragEvent) => {
      counter++;
      if (e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')) setDragging(true);
    };
    const onLeave = () => {
      counter = Math.max(0, counter - 1);
      if (counter === 0) setDragging(false);
    };
    const onDrop = () => { counter = 0; setDragging(false); };
    const onOver = (e: DragEvent) => { e.preventDefault(); };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragover', onOver);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragover', onOver);
    };
  }, []);

  const reqFilled = REQUIRED_SLOTS.filter((s) => s.file).length;
  const reqTotal = REQUIRED_SLOTS.filter((s) => !s.planned).length;
  const optFilled = OPTIONAL_SLOTS.filter((s) => s.file).length;
  const optTotal = OPTIONAL_SLOTS.length;

  return (
    <div className="shell" data-theme={t.theme} data-density={t.density} data-variant={t.variant}>
      <NavRail />
      <div className="upload-page">
        <TitleBar cluster={cluster} />
        <ClusterPicker clusters={CLUSTERS} active={activeCluster} setActive={setActiveCluster} />
        <div className="upload-body">
          <div className="upload-section">
            <div className="us-head">
              <span className="num">00</span>
              <span className="name">Master.xlsx</span>
              <span className="sub">эталон для сверки — общий для всех кластеров</span>
              <span className="meta">обновлён <b>{MASTER.modified}</b></span>
            </div>
            <MasterBlock master={MASTER} />
          </div>

          <div className="upload-section">
            <div className="us-head">
              <span className="num">01</span>
              <span className="name">Обязательные файлы</span>
              <span className="sub">экспорты из 1С — нужны для всех конфигов</span>
              <span className="meta">
                <b style={{ color: reqFilled === reqTotal ? 'var(--match)' : 'var(--override)' }}>
                  {reqFilled}/{reqTotal}
                </b> загружено
                {REQUIRED_SLOTS.some((s) => s.planned) && (
                  <> · <b style={{ color: 'var(--ink-3)' }}>2 запланированы</b></>
                )}
              </span>
            </div>
            <SlotTable slots={REQUIRED_SLOTS} required />
          </div>

          <div className="upload-section">
            <div className="us-head">
              <span className="num">02</span>
              <span className="name">Опциональные файлы</span>
              <span className="sub">используются отдельными конфигами — ИП, амортизация, расшифровки</span>
              <span className="meta"><b>{optFilled}/{optTotal}</b> загружено</span>
            </div>
            <SlotTable slots={OPTIONAL_SLOTS} required={false} />
          </div>

          <div className="upload-section" style={{ borderBottom: 0 }}>
            <div className="us-head">
              <span className="num">03</span>
              <span className="name">Курсы валют</span>
              <span className="sub">общий справочник для всех кластеров</span>
              <span className="meta">источник: <b>НБ РК на конец периода</b></span>
            </div>
            <RatesPanel rates={RATES} />
          </div>
        </div>
        {dragging && (
          <div className="drop-overlay">
            <div className="drop-card">
              <div className="dco-glyph">⤓</div>
              <div className="dco-title">Отпустите файлы</div>
              <div className="dco-sub">.xlsx будут автоматически распределены по слотам<br />обязательных и опциональных файлов кластера <b>{cluster.label}</b></div>
            </div>
          </div>
        )}
      </div>

      <TweaksPanel>
        <TweakSection label="Тема" />
        <TweakRadio label="Theme" value={t.theme}
          options={['paper', 'light', 'dark']}
          onChange={(v) => setTweak('theme', v)} />
        <TweakSection label="Вариант" />
        <TweakRadio label="Variant" value={t.variant}
          options={['safe', 'bold']}
          onChange={(v) => setTweak('variant', v)} />
        <TweakSection label="Плотность" />
        <TweakRadio label="Density" value={t.density}
          options={['compact', 'regular', 'comfy']}
          onChange={(v) => setTweak('density', v)} />
      </TweaksPanel>
    </div>
  );
}
